import { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  Button,
  TableRow,
  TableCell,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DataTable from '@/components/DataTable/DataTable';
import { ClientBankAccountsApi } from '@/api/clients/client-bank-accounts';
import ClientBankAccountStatus from '@/components/ClientBankAccounts/ClientBankAccountStatus';
import { useConfirmation } from '@/context/ConfirmationContext';
import ClientBankAccountFormDialog from '@/views/Organizations/ClientBankAccounts/ClientBankAccountFormDialog';
import { formatTimestamp } from '@/utils/date/date';
import useFormValidation from '@/hooks/useFormValidation';

const ClientBankAccountsTab = ({ organizationId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dataTableRef = useRef(null);
  const { requestConfirmation } = useConfirmation();
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const {
    fieldErrors,
    generalErrors,
    setBackendErrors,
    clearAllErrors,
  } = useFormValidation();

  const handleDelete = async (accountId, displayName) => {
    const confirmMessage = t('clientBankAccounts.confirmDelete', {
      name: displayName,
    });

    requestConfirmation(confirmMessage, async () => {
      setLoading(true);
      try {
        await ClientBankAccountsApi.deleteClientBankAccount(
          organizationId,
          accountId,
        );
        toast.success(t('clientBankAccounts.deleteSuccess'));
        dataTableRef.current?.refresh();
      } catch (err) {
        console.error('Error deleting bank account:', err);
      } finally {
        setLoading(false);
      }
    });
  };

  const handleEdit = (accountId) => {
    navigate(
      `/admin/organizations/${organizationId}/bank-accounts/${accountId}`,
    );
  };

  const handleCreate = () => {
    clearAllErrors();
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    clearAllErrors();
  };

  const handleSave = async (formData) => {
    clearAllErrors();
    setLoading(true);
    try {
      const response = await ClientBankAccountsApi.createClientBankAccount(
        organizationId,
        formData,
      );
      toast.success(t('clientBankAccounts.createSuccess'));
      handleCloseDialog();
      dataTableRef.current?.refresh();
      navigate(
        `/admin/organizations/${organizationId}/bank-accounts/${response.data.data.id}`,
      );
    } catch (err) {
      setBackendErrors(err.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const columnFilters = useMemo(
    () => [
      {
        key: 'search',
        label: t('clientBankAccounts.search'),
        filterType: 'search',
        placeholder: t('clientBankAccounts.searchPlaceholder'),
        debounce: true,
        debounceDelay: 300,
      },
      {
        key: 'status',
        label: t('clientBankAccounts.table.status'),
        filterType: 'select',
        options: [
          { value: '', label: t('clientBankAccounts.filters.allStatuses') },
          { value: 'active', label: t('clientBankAccounts.filters.active') },
          {
            value: 'inactive',
            label: t('clientBankAccounts.filters.inactive'),
          },
        ],
      },
    ],
    [t],
  );

  const columns = [
    {
      key: 'display_name',
      label: t('clientBankAccounts.table.displayName'),
      sortable: true,
    },
    {
      key: 'business_payee_name',
      label: t('clientBankAccounts.table.accountName'),
      sortable: true,
    },
    {
      key: 'bank_name',
      label: t('clientBankAccounts.table.bankName'),
      render: (value) => value || '-',
    },
    {
      key: 'provider',
      label: t('clientBankAccounts.table.provider'),
      render: (value) => {
        if (!value) return '-';
        return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
      },
    },
    {
      key: 'account_last_four',
      label: t('clientBankAccounts.table.last4'),
      render: (value) => `****${value}`,
    },
    {
      key: 'routing_number',
      label: t('clientBankAccounts.table.routingNumber'),
    },
    {
      key: 'created_at',
      label: t('clientBankAccounts.table.createdAt'),
      sortable: true,
      render: (value) => formatTimestamp(value),
    },
    {
      key: 'updated_at',
      label: t('clientBankAccounts.table.updatedAt'),
      sortable: true,
      render: (value) => formatTimestamp(value),
    },
    {
      key: 'deactivated_at',
      label: t('clientBankAccounts.table.status'),
      render: (value) => <ClientBankAccountStatus deactivatedAt={value} />,
    },
    {
      key: 'actions',
      label: t('clientBankAccounts.table.actions'),
      sortable: false,
      align: 'right',
      render: (_, row) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
          <Tooltip title={t('clientBankAccounts.editTooltip')}>
            <IconButton
              size='small'
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(row.id);
              }}>
              <EditIcon fontSize='small' />
            </IconButton>
          </Tooltip>
          {!row.deactivated_at && (
            <Tooltip title={t('clientBankAccounts.deleteTooltip')}>
              <IconButton
                size='small'
                color='error'
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(row.id, row.display_name);
                }}
                disabled={loading}>
                <DeleteIcon fontSize='small' />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ pt: 2 }}>
      <DataTable
        ref={dataTableRef}
        columns={columns}
        endpoint={`/organizations/${organizationId}/bank-accounts`}
        initialSort={{ key: 'created_at', order: 'desc' }}
        pageSize={50}
        autoFetch={true}
        enableColumnPicker={true}
        enableSorting={true}
        enableFilters={true}
        columnFilters={columnFilters}
        externalLoading={loading}
        renderToolbar={() => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
            <Button
              variant='outlined'
              color='primary'
              startIcon={<AddIcon />}
              onClick={handleCreate}>
              {t('clientBankAccounts.createButton')}
            </Button>
          </Box>
        )}
        renderEmptyState={() => (
          <TableRow>
            <TableCell colSpan={columns.length} align='center' sx={{ py: 8 }}>
              <Typography variant='body1' color='text.secondary'>
                {t('clientBankAccounts.noData')}
              </Typography>
            </TableCell>
          </TableRow>
        )}
      />

      <ClientBankAccountFormDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        organizationId={organizationId}
        onSave={handleSave}
        loading={loading}
        fieldErrors={fieldErrors}
        generalErrors={generalErrors}
      />
    </Box>
  );
};

export default ClientBankAccountsTab;
